"use client"

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { PARTIAL_SCORE_TOOLTIP } from "@/lib/grading/copy"
import {
  GRADING_PARTIAL_RING_STROKE,
  GRADING_PARTIAL_RING_TRACK,
} from "@/lib/grading/table-styles"
import { cn } from "@/lib/utils"

const SIZE = 10
const STROKE = 1.5
const RADIUS = (SIZE - STROKE) / 2
const CIRCUMFERENCE = 2 * Math.PI * RADIUS

/** Fixed trailing slot so partial rings do not shift fully scored numbers. */
export const PARTIAL_COMPLETION_SLOT_CLASS = cn(
  "inline-flex size-6 min-h-6 min-w-6 shrink-0 items-center justify-center",
)

/** Proportional ring: arc = questions with points / active questions on the submission. */
export function PartialCompletionMark({
  scoredCount,
  totalCount,
  className,
}: {
  scoredCount: number
  totalCount: number
  className?: string
}) {
  if (totalCount <= 0 || scoredCount >= totalCount) return null

  const fraction = scoredCount / totalCount
  const dashOffset = CIRCUMFERENCE * (1 - fraction)
  const ariaLabel = `${scoredCount} of ${totalCount} questions scored. ${PARTIAL_SCORE_TOOLTIP}`

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          role="img"
          aria-label={ariaLabel}
          tabIndex={0}
          className={cn(
            "inline-flex size-6 min-h-6 min-w-6 items-center justify-center rounded-md",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            className,
          )}
        >
          <svg
            width={SIZE}
            height={SIZE}
            viewBox={`0 0 ${SIZE} ${SIZE}`}
            aria-hidden="true"
            className="-rotate-90 shrink-0"
          >
            <circle
              cx={SIZE / 2}
              cy={SIZE / 2}
              r={RADIUS}
              fill="none"
              strokeWidth={STROKE}
              style={GRADING_PARTIAL_RING_TRACK}
            />
            <circle
              cx={SIZE / 2}
              cy={SIZE / 2}
              r={RADIUS}
              fill="none"
              stroke={GRADING_PARTIAL_RING_STROKE}
              strokeWidth={STROKE}
              strokeLinecap="round"
              strokeDasharray={CIRCUMFERENCE}
              strokeDashoffset={dashOffset}
            />
          </svg>
        </span>
      </TooltipTrigger>
      <TooltipContent side="top">{ariaLabel}</TooltipContent>
    </Tooltip>
  )
}
